const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;

// Use window.posthog initialized by the HTML snippet in index.html.
// The posthog-js npm module creates a separate uninitialized singleton
// that doesn't share state with window.posthog, so we proxy to the global.
const noop = () => {};
const posthog = POSTHOG_KEY
  ? new Proxy(
      {},
      {
        get: (_target, prop) => {
          const ph = typeof window !== 'undefined' && window.posthog;
          if (ph && typeof ph[prop] === 'function') {
            return ph[prop].bind(ph);
          }
          return ph ? ph[prop] : noop;
        },
      },
    )
  : new Proxy(
      {},
      {
        get: () => noop,
      },
    );

/**
 * Identifies and sets user properties for PostHog tracking
 * @param {Object} options - User properties
 * @param {Object} options.agency - Agency object with id, agencyCode, name
 * @param {Object} options.agent - Agent object with id, email, fullName
 * @param {Object} options.client - Client object with id, email, name/fullName, dealId, dealIdSource
 * @param {string} options.userType - Type of user ('agent' or 'client')
 */
export const identifyPostHogUser = ({ agency, agent, client, userType }) => {
  // || not ?? — client emails are routinely empty strings, which must fall through to the id form
  const distinctId = userType === 'agent' ? agent?.email : client?.email || (client?.id ? `client-${client.id}` : null);

  if (!distinctId) {
    return;
  }

  // Agent app uses agency.name, client app uses agency.agencyName
  const agencyName = agency?.name || agency?.agencyName;

  // Super properties — attached to every event for easy filtering/breakdown.
  // Absent optional props are unregistered so a cleared client (or missing
  // agency) doesn't leave a stale value on subsequent events.
  const superProps = {};
  if (userType) superProps.user_type = userType;
  if (agency?.agencyCode) superProps.agency_code = agency.agencyCode;
  if (agencyName) superProps.agency_name = agencyName;
  if (agent?.email) superProps.agent_email = agent.email;
  if (client?.email) superProps.client_email = client.email;
  ['agency_code', 'agency_name', 'agent_email', 'client_email'].forEach(key => {
    if (!(key in superProps)) posthog.unregister(key);
  });
  posthog.register(superProps);

  const userProperties = {};

  // Agency properties
  if (agency?.id) userProperties.agency_id = agency.id;
  if (agency?.agencyCode) userProperties.agency_code = agency.agencyCode;
  if (agencyName) userProperties.agency_name = agencyName;

  // Agent properties
  if (agent?.id) userProperties.agent_id = agent.id;
  if (agent?.email) userProperties.agent_email = agent.email;
  if (agent?.fullName) userProperties.agent_name = agent.fullName;

  // Client properties
  if (client?.id) userProperties.client_id = client.id;
  if (client?.email) userProperties.client_email = client.email;
  if (client?.name || client?.fullName) userProperties.client_name = client.name || client.fullName;
  if (client?.dealId) userProperties.deal_id = client.dealId;
  if (client?.dealIdSource) userProperties.deal_id_source = client.dealIdSource;

  // PostHog displays persons using top-level `email` and `name` properties
  const email = userType === 'agent' ? agent?.email : client?.email;
  const name = userType === 'agent' ? agent?.fullName : client?.name || client?.fullName;
  if (email) userProperties.email = email;
  if (name) userProperties.name = name;

  // User type
  if (userType) userProperties.user_type = userType;

  posthog.identify(distinctId, userProperties);

  // Group analytics — agency-level tracking (keyed on agency.id, unique per tenant)
  if (agency?.id) {
    const groupProperties = {};
    if (agencyName) groupProperties.name = agencyName;
    if (agency?.agencyCode) groupProperties.agency_code = agency.agencyCode;
    posthog.group('agency', agency.id, groupProperties);
  }
};

/**
 * Resets the PostHog user (call on logout)
 */
export const resetPostHogUser = () => {
  posthog.reset();
};

// The super properties clearMasqueradeContext unregisters. A property that setMasqueradeContext
// registers and this list omits outlives the masquerade and marks every later event as one.
const MASQUERADE_PROPS = ['masq', 'masq_by'];

/**
 * Marks events as driven by a superadmin acting as another user (call on masquerade entry)
 */
export const setMasqueradeContext = superadminEmail => {
  // distinct_id and agent_email already carry the masqueraded user, so the superadmin is
  // the only party to this that PostHog would otherwise have no record of. Super
  // properties rather than person properties: a person property describes who someone is
  // now and cannot mark an event that has already been captured.
  const props = { masq: true };
  if (superadminEmail) props.masq_by = superadminEmail;
  posthog.register(props);
};

/**
 * Clears the masquerade markers (call on masquerade exit)
 */
export const clearMasqueradeContext = () => {
  // identifyPostHogUser only unregisters the keys it owns, so these outlive the identify
  // that runs when the original user is restored.
  MASQUERADE_PROPS.forEach(prop => posthog.unregister(prop));
};
